import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";

import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Spinner";
import { cn } from "@/lib/utils";
import { useTelegramBackButton } from "@/utils/BackButton";
import { impactOccuer } from "@/utils/hapticEvents";
import { useGetNotifications } from "../services/getNotifications";
import { groupNotifications } from "../libs/groupNotifications";
import { NotificationDrawer } from "./NotificationDrawer";
import { INotification } from "../types/Notification";

const GROUP_TITLE_KEY = {
  today: "today",
  yesterday: "yesterday",
  rest: "notifications-rest",
} as const;

/**
 * Экран «Уведомления»: три секции по дате, карточка — дата и заголовок,
 * непрочитанное отмечено красной точкой перед заголовком. Тап по карточке
 * помечает его прочитанным и открывает шторку с полным текстом; маркер в
 * шторке — по состоянию на момент открытия.
 */
export const Notification = () => {
  const { t } = useTranslation();
  const {
    notifications,
    isLoading,
    isError,
    refetch,
    hasMore,
    loadMore,
    isLoadingMore,
    markAsRead,
  } = useGetNotifications();

  // Подгрузка следующей страницы «маячком» под списком — как в заказах.
  const sentinel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasMore || isLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      },
      { rootMargin: "160px" },
    );
    observer.observe(node);

    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, loadMore, notifications.length]);
  const [opened, setOpened] = useState<{
    id: INotification["id"];
    wasUnread: boolean;
  } | null>(null);
  const openedNotification =
    notifications.find((item) => item.id === opened?.id) ?? null;

  const openNotification = (item: INotification) => {
    impactOccuer("light");
    setOpened({ id: item.id, wasUnread: !item.isRead });
    markAsRead(item.id);
  };

  useTelegramBackButton("/");

  const groups = groupNotifications(notifications);

  return (
    <div className="flex h-full flex-col overflow-y-auto px-4 pt-4 pb-10">
      {/* «Назад» — нативная кнопка Telegram (useTelegramBackButton), своей
          в шапке нет. */}
      <h1 className="text-display-sm text-center">{t("notifications")}</h1>

      {isLoading ? (
        <div className="flex flex-col gap-3 pt-6">
          <Skeleton className="h-5 w-24 rounded-md" />
          <Skeleton className="h-[72px] w-full rounded-2xl" />
          <Skeleton className="h-[72px] w-full rounded-2xl" />
        </div>
      ) : isError ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
          <p className="text-body-base-medium text-text-secondary">
            {t("somethingWentWrong")}
          </p>
          <Button
            type="button"
            onClick={() => refetch()}
            className="h-12 rounded-xl bg-button-secondary px-6 text-text-primary shadow-none"
          >
            <p className="text-body-base-semibold">{t("retry")}</p>
          </Button>
        </div>
      ) : groups.length === 0 ? (
        // Пустое состояние — как у корзины: картинка и две строки.
        <div className="flex flex-1 flex-col items-center justify-center space-y-4">
          <img
            src="/assets/lavashboy.png"
            alt=""
            className="h-[120px] w-[120px] rounded-xl"
          />
          <div className="flex flex-col space-y-1 text-center">
            <p className="text-body-base-medium text-text-secondary">
              {t("no-notifications")}
            </p>
            <p className="max-w-[200px] text-body-sm-regular text-text-secondary">
              {t("no-notifications-description")}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-6 pt-6">
          {groups.map((group) => (
            <section key={group.key} className="flex flex-col gap-3">
              <h2 className="text-body-base-semibold text-text-secondary">
                {t(GROUP_TITLE_KEY[group.key])}
              </h2>
              <div className="flex flex-col gap-3">
                {group.items.map((item) => (
                  <Card
                    key={item.id}
                    onClick={() => openNotification(item)}
                    className="flex flex-col gap-1 rounded-2xl border-none bg-white p-4 shadow-none"
                  >
                    <p className="text-caption-sm-medium text-text-tertiary">
                      {format(new Date(item.createdAt), "dd.MM.yyyy HH:mm")}
                    </p>
                    {/* Картинка над заголовком — только если бэкенд её
                        отдал; в макете стандартной карточки её нет. */}
                    {item.image && (
                      <div className="aspect-[343/200] w-full overflow-hidden rounded-xl bg-fill-background">
                        <img
                          src={item.image}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      </div>
                    )}
                    <div className="flex flex-row items-center gap-2">
                      {/* Точка непрочитанного. Место под неё не резервируем:
                          в макете прочитанный заголовок стоит у левого края. */}
                      <span
                        className={cn(
                          "h-2 w-2 shrink-0 rounded-full bg-fill-danger",
                          item.isRead && "hidden",
                        )}
                      />
                      <p className="min-w-0 break-words text-body-base-semibold text-text-primary">
                        {item.title}
                      </p>
                    </div>
                    <p className="line-clamp-2 break-words text-body-sm-regular text-text-secondary">
                      {item.shortDescription || item.description}
                    </p>
                  </Card>
                ))}
              </div>
            </section>
          ))}
          {hasMore && (
            <div
              ref={sentinel}
              className="flex h-16 items-center justify-center"
            >
              {isLoadingMore && <Spinner />}
            </div>
          )}
        </div>
      )}
      <NotificationDrawer
        notification={openedNotification}
        wasUnread={opened?.wasUnread ?? false}
        onClose={() => setOpened(null)}
      />
    </div>
  );
};

export default Notification;
